import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory30() {
  const { lesson, course, accent } = findLesson("music-theory", "30");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT30-Chord-Charts-Animated.mp4"
        caption="Reading chord charts — chord symbol → notes on the keyboard"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="30" eyebrow="Music Theory · Chapter Thirty" title="How to read chord charts">
        <p>
          Sheet music is precise but slow. It tells you every note, every voicing, every
          rhythm. <strong>Chord charts</strong> are the harmonic equivalent of road
          signs: a stream of compact symbols that tell a player <em>what</em> the chord
          is, not how to voice it. Capable musicians can sight-read a chart in any key,
          choose their own inversions, accompany in any pattern, and solo over each chord
          — all from a couple of letters and a number.
        </p>
        <p>
          Charts are also the universal shorthand of the working music world. Real Books
          (the jazz fake books) are nothing but melody plus charts. Studio sessions hand
          out charts, not staves. Every gigging musician needs to read them at sight. So
          this chapter is the symbol vocabulary: triads, sevenths, sus chords, add
          chords, sixths, slash chords and the polyphonic poly-chords at the end.
        </p>
        <MTQuote>
          The way I like to think as a player is, if I'm reading, if I'm sight reading the
          melody, I want to kind of intuitively figure out the harmony, because I can
          accompany in different patterns. I don't have to follow the accompaniment pattern
          printed in the notation script.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The alphabet" title="Twelve letters cover all twelve notes">
        <Prose>
          <p>
            All chord charts use the seven natural letters — <strong>A B C D E F G</strong> —
            plus sharps and flats for the five black keys. Every black key has two
            spellings: F♯ = G♭, A♭ = G♯, A♯ = B♭, D♭ = C♯, D♯ = E♭. Which name appears
            depends on the key and the harmonic context.
          </p>
          <p>
            <strong>Capital letter alone = major triad.</strong> If a chart says
            <span className="font-mono"> E</span>, the chord is <em>E major</em>. Always.
            Whatever instrument, whatever inversion, whatever voicing — the symbol
            <span className="font-mono"> E</span> means a major triad on E.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Triads" title="Major, minor, diminished, augmented, suspended">
        <TheoryBox label="Triad symbols">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>E</strong> &nbsp;→&nbsp; E major</div>
            <div><strong>Em</strong> &nbsp;or&nbsp; <strong>E−</strong> &nbsp;→&nbsp; E minor (jazz convention uses the minus sign)</div>
            <div><strong>Edim</strong> &nbsp;or&nbsp; <strong>E°</strong> &nbsp;→&nbsp; E diminished (1, ♭3, ♭5)</div>
            <div><strong>Eaug</strong> &nbsp;or&nbsp; <strong>E+</strong> &nbsp;→&nbsp; E augmented (1, 3, ♯5)</div>
            <div><strong>Esus2</strong> &nbsp;→&nbsp; E suspended 2 (1, 2, 5 — no third)</div>
            <div><strong>Esus4</strong> &nbsp;→&nbsp; E suspended 4 (1, 4, 5 — no third)</div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            <strong>Sus</strong> chords replace the third with the second or fourth. The
            third is what makes a chord major or minor — remove it and the chord becomes
            <em> open</em>, neither happy nor sad, hungry to resolve. Sus4 typically
            wants to fall back to the major chord (the 4 drops a half-step to the major
            3rd). Sus2 floats more freely.
          </p>
        </Prose>

        <ChordBuilder root="E" families={["triad"]} />
      </Chapter>

      <Chapter eyebrow="Add chords" title="Adding a colour without a seventh">
        <Prose>
          <p>
            <strong>Add</strong> chords keep the third (so they're <em>not</em> sus
            chords) and add an extra note — usually the 2/9 or the 4. The convention is
            simple: if you see <em>add</em> followed by a number, that interval is
            inserted into the otherwise-normal triad.
          </p>
        </Prose>

        <TheoryBox label="Add-chord symbols">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>Eadd2</strong> &nbsp;→&nbsp; E G♯ B + F♯</div>
            <div><strong>Eadd4</strong> &nbsp;→&nbsp; E G♯ B + A</div>
            <div><strong>Em(add9)</strong> &nbsp;→&nbsp; E G B + F♯ &nbsp;<span className="text-nsm-dim">(some prefer "add9" once it's voiced an octave up)</span></div>
            <div><strong>E(♭2)</strong> &nbsp;→&nbsp; E G♯ B + F &nbsp;<span className="text-nsm-dim">(very Middle-Eastern colour)</span></div>
            <div><strong>E(♯5)</strong> &nbsp;or&nbsp; <strong>E(♭6)</strong> &nbsp;→&nbsp; E with a raised 5th — same as augmented but written as an alteration</div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Note: <strong>add</strong> is only used for 2 and 4. Sixths and sevenths get
            their own naming convention — there's no such thing as "C add 6" (just write
            <span className="font-mono"> C6</span>) or "Cm add 6" (just
            <span className="font-mono"> Cm6</span>).
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Sixths and sevenths" title="The most-used four-note chords">
        <TheoryBox label="6th and 7th symbols">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>E6</strong> &nbsp;→&nbsp; E major 6th &nbsp;<span className="text-nsm-dim">(major triad + major 6)</span></div>
            <div><strong>Em6</strong> &nbsp;→&nbsp; E minor 6th &nbsp;<span className="text-nsm-dim">(minor triad + major 6)</span></div>
            <div><strong>Emaj7</strong> &nbsp;or&nbsp; <strong>E△7</strong> &nbsp;or&nbsp; <strong>E△</strong> &nbsp;→&nbsp; E major 7</div>
            <div><strong>E7</strong> &nbsp;→&nbsp; E dominant 7th &nbsp;<span className="text-nsm-dim">(major triad + ♭7)</span></div>
            <div><strong>Em7</strong> &nbsp;or&nbsp; <strong>E−7</strong> &nbsp;→&nbsp; E minor 7</div>
            <div><strong>Em(maj7)</strong> &nbsp;→&nbsp; E minor with major 7th &nbsp;<span className="text-nsm-dim">(spy-thriller chord)</span></div>
            <div><strong>Em7♭5</strong> &nbsp;or&nbsp; <strong>Eø</strong> &nbsp;→&nbsp; E half-diminished &nbsp;<span className="text-nsm-dim">(diminished triad + ♭7)</span></div>
            <div><strong>Edim7</strong> &nbsp;or&nbsp; <strong>E°7</strong> &nbsp;→&nbsp; E fully diminished 7th &nbsp;<span className="text-nsm-dim">(stack of ♭3s)</span></div>
            <div><strong>Eaug7</strong> &nbsp;or&nbsp; <strong>E7♯5</strong> &nbsp;→&nbsp; E7 with augmented 5th</div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            <strong>The number 7 by itself always means ♭7</strong>. So
            <span className="font-mono"> E7</span> is a major triad with a flat 7
            (E&nbsp;G♯&nbsp;B&nbsp;D), not a major 7th. To get the major 7th you must
            write <span className="font-mono">Emaj7</span> or
            <span className="font-mono"> E△7</span>. The triangle (△) is jazz shorthand
            and saves space on a chart.
          </p>
        </Prose>

        <ChordBuilder root="E" families={["seventh"]} />
      </Chapter>

      <Chapter eyebrow="Extensions" title="9, 11, 13 — and the rule about 7s">
        <Prose>
          <p>
            Once you go beyond the seventh, you enter <strong>extensions</strong>: 9, 11,
            13. These are tones stacked above the octave — the 9 is the same pitch class
            as the 2, the 11 as the 4, the 13 as the 6.
          </p>
        </Prose>

        <TheoryBox label="The extension rule">
          Whenever a chart says 9, 11 or 13, there is also a 7 in the chord — major or
          dominant. <span className="font-mono">E9</span> is E + ♭7 + 9 (a dominant 9th).
          <span className="font-mono"> Emaj9</span> is E + maj7 + 9.
          <span className="font-mono"> Em11</span> is E minor + ♭7 + 9 + 11. The 7 is
          always implied — it's what makes the chord an extension and not an add.
        </TheoryBox>

        <Prose>
          <p>
            That distinction is the difference between an add chord and an extension.
            <span className="font-mono"> Eadd9</span> = triad + 9, <em>no 7</em>.
            <span className="font-mono"> E9</span> = dominant 7 + 9, <em>with 7</em>.
            Two very different colours.
          </p>
        </Prose>

        <ChordBuilder root="E" families={["triad", "seventh", "extension"]} />
      </Chapter>

      <Chapter eyebrow="Slash chords" title="A triad with a chosen bass">
        <Prose>
          <p>
            A <strong>slash chord</strong> writes the triad above the slash and the
            <em> bass note</em> below it. The bass note can be a chord tone (giving an
            inversion) or a non-chord tone (giving a more interesting harmonic colour).
          </p>
        </Prose>

        <TheoryBox label="Slash-chord examples">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>A/C♯</strong> &nbsp;→&nbsp; A major triad over a C♯ bass &nbsp;<span className="text-nsm-dim">(first inversion)</span></div>
            <div><strong>A/E</strong> &nbsp;→&nbsp; A major over E bass &nbsp;<span className="text-nsm-dim">(second inversion)</span></div>
            <div><strong>A/G</strong> &nbsp;→&nbsp; A major over G bass &nbsp;<span className="text-nsm-dim">(non-chord tone — gives a dominant 7th sound from below)</span></div>
            <div><strong>A/B</strong> &nbsp;→&nbsp; A major over B bass &nbsp;<span className="text-nsm-dim">(very common pop colour, almost a sus11)</span></div>
            <div><strong>F/A</strong> &nbsp;→&nbsp; F major over A &nbsp;<span className="text-nsm-dim">(the Hey-Jude-style first inversion)</span></div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            A bass player reads the bottom of the slash. A piano or guitar player reads
            the top. Both put their note in the right register and the chord sounds as
            written. That role separation is exactly why slash notation exists.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Beyond" title="Polychords and the rest">
        <Prose>
          <p>
            <strong>Polychords</strong> stack one chord over another — for example, D
            major over C major, written D / C with a horizontal line between them. They
            crop up in modern jazz, film scoring and orchestral writing where two
            distinct triads collide for a deliberately complex effect. We'll meet them
            properly in a later chapter; for now, just know the symbol.
          </p>
        </Prose>

        <Glossary>
          <Term name="Triad" def="A 3-note chord — root, third, fifth. Major (M3+P5), minor (m3+P5), diminished (m3+♭5), augmented (M3+♯5)." />
          <Term name="Sus" def="Suspended. The 3rd is replaced by the 2 (sus2) or 4 (sus4). No major/minor identity until it resolves." />
          <Term name="Add" def="An extra note layered on top of a triad without removing the third. Used for 2/9 or 4." />
          <Term name="Slash chord" def="Triad over a chosen bass note, written upper/lower. The bass needn't be a chord tone." />
          <Term name="Extension" def="The 9, 11, or 13 — stacked above an implied 7th. 'E9' always includes the ♭7." />
          <Term name="Polychord" def="Two complete chords stacked — e.g. D over C — written as a vertical fraction." />
        </Glossary>

        <TheoryBox label="The whole symbol set, on one page">
          <div className="text-[12px] text-nsm-dim">
            Capital alone = major. Add <strong>m</strong> or <strong>−</strong> for minor.
            Add <strong>°</strong> or <strong>dim</strong> for diminished. Add
            <strong> +</strong> or <strong>aug</strong> for augmented. Add
            <strong> sus2/sus4</strong> to suspend. Add <strong>add2/add4</strong> to
            add. Add <strong>6</strong> for the sixth. Add <strong>7</strong> for the
            ♭7, <strong>maj7</strong> or <strong>△</strong> for the major 7. Layer
            <strong> 9, 11, 13</strong> on top of any 7th. Slash <strong>/note</strong>
            for the bass. Stack vertically for polychords. That's the alphabet.
          </div>
        </TheoryBox>
        <NoteImage
          src="/notes/music-theory/finals/30b-chord-charts-cleaner-version.png"
          caption="Jason's chord-chart legend: 7 natural letters (A B C D E F G) plus 5 sharps/flats (F♯/G♭, G♯/A♭, A♯/B♭, C♯/D♭, D♯/E♭). Triad qualities are appended directly: C means C major; Cm or C- means C minor; C° or Cdim means diminished; C+ or Caug means augmented. Reading a chart is just stitching these symbols together over a beat."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-31"
        label="Chapter 31 · Roman Numeral Analysis"
        why="Charts give you the absolute names of chords. Roman numerals give you their relative function in a key — and let you transpose any chart to any key in seconds."
      />
    </LessonShell>
  );
}
